import { EventAggregator } from 'aurelia-event-aggregator';
import { autoinject } from 'aurelia-framework';
import {
	NavigationInstruction,
	Next,
	PipelineStep,
	Redirect,
} from 'aurelia-router';
import { StateHistory, Store } from 'aurelia-store';
import { result } from 'lodash-es';

import { App, AppEvents } from '~/app';
import { AppRouteID } from '~/app-routes';
import { RouteConfig } from '~/app-routes';
import { AppState } from '~/app-store';
import { Authentication } from '~/authentication/authentication';
import { CommonURLParam } from '~/constants';
import { LoginActions } from '~/login/login-actions';
import { OKTA_PRE_REDIRECT_URL, OktaLogin } from '~/login/okta-login';
import { Logger } from '~/utils/logger';
import { RedirectUtils } from '~/utils/redirect-utils';

@autoinject
export class AuthorizeStep implements PipelineStep {
	constructor(
		private readonly store: Store<StateHistory<AppState>>,
		private eventAggregator: EventAggregator,
		private readonly authentication: Authentication,
		private readonly oktaLogin: OktaLogin,
	) {}

	public async run(
		navigationInstruction: NavigationInstruction,
		next: Next,
	): Promise<any> {
		let isAccessingPageForLoggedInUser: boolean = false;
		let handleRedirectFromSso: boolean = false;
		let isLoginPage: boolean = false;
		for (const inst of navigationInstruction.getAllInstructions()) {
			const routeConfig: RouteConfig = inst.config;
			if (routeConfig.settings.roles !== undefined) {
				isAccessingPageForLoggedInUser =
					isAccessingPageForLoggedInUser ||
					routeConfig.settings.roles.indexOf('LoginUser') !== -1;
			}
			if (routeConfig.route === AppRouteID.SsoLogin) {
				handleRedirectFromSso = true;
			}
			if (!routeConfig.settings || !routeConfig.settings.data) {
				continue;
			}
			isLoginPage =
				isLoginPage || routeConfig.settings.data.isLoginPage === true;
		}

		const isLoggedIn = this.authentication.isLoggedIn();
		const isLoginExpired = this.authentication.isLoginExpired();

		if (handleRedirectFromSso) {
			// If we're trying to navigate to `sso-login`, this can be initiated by the WEBUI
			// login screen (not logged in) or through the Okta portal (may already be logged in).
			await this.oktaLogin.attemptOktaLogin();
			let redirectUrl = sessionStorage.getItem(OKTA_PRE_REDIRECT_URL);
			sessionStorage.removeItem(OKTA_PRE_REDIRECT_URL);
			if (!redirectUrl) {
				// redirectUri may not be in storage if loading WEBUI from Okta portal. If so, redirect to empty path
				redirectUrl = window.location.origin;
			}
			return this.cancelPipelineWithRedirect(next, redirectUrl);
		}

		if (isAccessingPageForLoggedInUser) {
			// auth options may not be set even when logged in if we are opening a form in a new tab
			// fetch auth options here so they are ready to use next time we need an access token
			if (OktaLogin.isLoggedIn() && !OktaLogin.oktaAuthOptionsAreSet()) {
				await this.oktaLogin.getOktaAuthOptions();
				return this.cancelPipelineWithRedirect(next);
			}

			if (RedirectUtils.hasSsoParamEnabled() && !isLoggedIn) {
				// remove `sso` param before saving to sessionStorage
				const urlParts: string[] = window.location.href.split('?');
				const params: URLSearchParams = new URLSearchParams(
					urlParts[1],
				);
				params.delete(CommonURLParam.SSO);
				const redirectUrl: string =
					urlParts[0] + `?${params.toString()}`;
				const encodedUrlWithRedirectFrom =
					RedirectUtils.getEncodedUrlWithRedirectFrom(redirectUrl);
				await this.oktaLogin.redirectToOkta(encodedUrlWithRedirectFrom);
				return next.cancel();
			}

			if (!isLoggedIn) {
				this.logout();
				return this.cancelPipelineWithRedirect(next);
			} else if (isLoginExpired) {
				// Note: WEBUI-1715 York
				// Actually it is unnecessary to refresh token here since we always have a /api/settings/user request in
				// InitialLoadStep which will try to refresh access token before completion of the pipeline step.
				const retryPromise = new Promise<any>((resolve, reject) => {
					this.eventAggregator.subscribeOnce(
						AppEvents.TokenRefreshed,
						(refreshedResult: boolean) => {
							if (refreshedResult) {
								resolve();
							} else {
								reject();
							}
						},
					);
				})
					.then(() => {
						this.login();
						return next();
					})
					.catch(() => {
						// Don't need to publish logout event again since it is done when refresh token failed.
						return this.cancelPipelineWithRedirect(next);
					});
				this.authentication.tryToRefreshToken('authorize');
				return retryPromise;
			}
			this.login();
			return next();
		}
		// NOTE: Jake: 2019-11-22
		// If on login page and if logged in, redirect to base URL.
		// (which will end up redirecting to the first defined app route)
		if (isLoginPage && isLoggedIn && !isLoginExpired) {
			this.login();
			const redirectFromURL = RedirectUtils.getRedirectUrl();
			return next.cancel(
				new Redirect(redirectFromURL, { replace: true }),
			);
		}
		return next();
	}

	private login() {
		if (!result(this, 'store.state.source.value.present.isLoggedIn')) {
			this.store.dispatch(LoginActions.LoginUser);
		}
	}

	private logout() {
		Logger.log('Not logged in! Redirecting to login ...');
		this.eventAggregator.publish(App.Events.Logout);
	}

	private cancelPipelineWithRedirect(
		next: Next,
		redirectUrl: string = window.location.href,
	): Promise<any> {
		const redirectURI =
			RedirectUtils.getEncodedUriWithRedirectFrom(redirectUrl);
		return next.cancel(new Redirect(redirectURI, { replace: true }));
	}
}
